import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  alpha,
  Fade,
  AlertTitle,
  Card
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from '../contexts/AuthContext';

console.log('Login page loaded - do not fetch chain data on this page');

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const { signInWithGoogle, error: authError } = useAuth();
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (authError) {
      setError(authError);
      setLoading(false);
    }
  }, [authError]);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      if (!result) {
        // User closed the popup
        setLoading(false);
        return;
      }
      
      // Success - don't set loading to false here as the auth state change will trigger a redirect
    } catch (err) {
      console.error('Sign in error:', err);
      setLoading(false);
      
      // Error is already set by the AuthContext
    }
  };

  // Add cleanup effect
  useEffect(() => {
    return () => {
      setError('');
      setLoading(false);
    };
  }, []);

  const getErrorMessage = () => {
    switch (error) {
      case 'unauthorized':
        return {
          title: 'Access Denied',
          message: 'You are not authorized to access this application. Please contact your administrator for access.',
          severity: 'error'
        };
      case 'popup-blocked':
        return {
          title: 'Popup Blocked',
          message: 'Please allow popups for this website and try again.',
          severity: 'warning'
        };
      case 'popup-closed':
        return {
          title: 'Sign-in Cancelled',
          message: 'The sign-in process was cancelled. Please try again.',
          severity: 'info'
        };
      case 'default':
        return {
          title: 'Authentication Failed',
          message: 'An error occurred during sign in. Please try again.',
          severity: 'error'
        };
      default:
        return null;
    }
  };

  const errorDetails = getErrorMessage();

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'background.default',
      }}
    >
      {/* Main Card Container - Campaign Style with Rounded Corners */}
      <Fade in={mounted} timeout={1000}>
        <Card
          elevation={0}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 0,
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'background.paper',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Main Content Container - Campaign Structure */}
          <Box sx={{ 
            width: '100%',
            height: '100%',
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'center', lg: 'stretch' },
            gap: { xs: 4, lg: 8 },
            textAlign: { xs: 'center', lg: 'left' },
            p: { xs: 2.5, sm: 3, lg: 3.5 },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'translateZ(0)',
          }}>
            
            {/* Left Column - Sign In Content (Campaign Style) */}
            <Box sx={{ 
              flex: '0 0 40%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              p: { xs: 2, sm: 3 },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              
              {/* Overline/Category - Campaign Style */}
              <Typography 
                variant="overline" 
                sx={{ 
                  fontFamily: 'StrawberrySansText',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  letterSpacing: '0.15em',
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
                  mb: 3,
                  display: 'block',
                  textTransform: 'uppercase'
                }}
              >
                HOTEL MANAGER
              </Typography>

              {/* Main Heading - Campaign Style */}
              <Typography 
                variant="h1" 
                sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 400,
                  fontSize: { xs: '2.5rem', sm: '3rem', lg: '3.5rem' },
                  lineHeight: 1.1,
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
                  mb: 4,
                  letterSpacing: '-0.02em'
                }}
              >
                Welcome to
                <Box component="span" sx={{ 
                  display: 'block',
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
                  fontWeight: 400,
                  fontSize: { xs: '2.5rem', sm: '3rem', lg: '3.5rem' }
                }}>
                  Hotel Manager
                </Box>
              </Typography>
              
              {/* Subtitle - Campaign Style */}
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'StrawberrySansText',
                  fontWeight: 500,
                  fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  lineHeight: 1.4,
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black',
                  mb: 4,
                  maxWidth: 480
                }}
              >
                Sign in to access your hotel portfolio
              </Typography>

              {/* Description - Campaign Style */}
              <Typography 
                variant="body1" 
                sx={{ 
                  fontFamily: 'StrawberrySansText',
                  fontWeight: 400,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.7,
                  color: theme => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.7)' 
                    : 'rgba(0, 0, 0, 0.7)',
                  mb: { xs: 3, lg: 4 },
                  maxWidth: 460
                }}
              >
                Secure authentication powered by Google. Your data is protected and encrypted.
              </Typography>

              {/* Error Alert */}
              {error && errorDetails && (
                <Fade in={true}>
                  <Alert 
                    severity={errorDetails.severity}
                    icon={error === 'unauthorized' ? <ErrorOutlineIcon /> : undefined}
                    sx={{ 
                      width: '100%', 
                      mb: 4,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: theme => alpha(theme.palette[errorDetails.severity].main, 0.1),
                      background: theme => alpha(theme.palette[errorDetails.severity].main, 0.05),
                      backdropFilter: 'blur(10px)',
                      '& .MuiAlert-icon': {
                        color: theme => theme.palette[errorDetails.severity].main,
                        fontSize: '24px'
                      },
                      '& .MuiAlert-message': {
                        width: '100%'
                      }
                    }}
                  >
                    <AlertTitle sx={{ 
                      fontFamily: 'StrawberrySansDisplay',
                      fontWeight: 500,
                      fontSize: '1rem',
                      mb: 0.5
                    }}>
                      {errorDetails.title}
                    </AlertTitle>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme => alpha(theme.palette.text.primary, 0.7),
                        fontFamily: 'StrawberrySansText',
                        lineHeight: 1.5,
                        fontSize: '0.875rem'
                      }}
                    >
                      {errorDetails.message}
                    </Typography>
                  </Alert>
                </Fade>
              )}

              {/* Sign In Button */}
              <Button
                variant="contained"
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  py: 2.5,
                  px: 4,
                  borderRadius: 28,
                  fontSize: '1rem',
                  textTransform: 'none',
                  fontFamily: 'StrawberrySansText',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  minHeight: 56,
                  width: '280px',
                  boxShadow: theme => `0 2px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: '1px solid',
                  borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.15),
                    borderColor: theme => alpha(theme.palette.primary.main, 0.3),
                    boxShadow: theme => `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.2),
                    boxShadow: theme => `0 1px 6px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                  '&:disabled': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                    color: theme => alpha(theme.palette.primary.main, 0.5),
                    borderColor: theme => alpha(theme.palette.primary.main, 0.1),
                    boxShadow: 'none',
                    transform: 'none',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? (
                  <CircularProgress 
                    size={24} 
                    sx={{ 
                      color: 'primary.main'
                    }} 
                  />
                ) : (
                  'Login with Strawberry SSO'
                )}
              </Button>
            </Box>

            {/* Right Column - Campaign Images (3 layered images with brand radius) */}
            <Box sx={{ 
              flex: 1,
              width: '100%',
              alignSelf: { xs: 'center', lg: 'stretch' },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
            }}>
              
              {/* Layer 1 - Outer Solid Color (0.5x radius) */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 2,
                overflow: 'hidden',
                zIndex: 1,
                background: theme => theme.palette.primary.main,
              }} />
              
              {/* Layer 2 - Middle Image (1x radius) */}
              <Box sx={{
                position: 'absolute',
                top: 60,
                left: 60,
                right: 60,
                bottom: 60,
                borderRadius: 4,
                overflow: 'hidden',
                zIndex: 2,
                backgroundImage: 'url(https://nordicchoicehotels.papirfly.no/readimage.aspx/asset.jpg?pubid=0ZcDyRerwvqu2BFzdCoMTA&quality=9)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 4,
                  background: theme => theme.palette.primary.main,
                  mixBlendMode: 'hue',
                  opacity: 1,
                  zIndex: 1,
                }
              }} />
              
              {/* Layer 3 - Inner Image (2x radius) */}
              <Box sx={{
                position: 'absolute',
                top: 120,
                left: 120,
                right: 120,
                bottom: 120,
                borderRadius: 8,
                overflow: 'hidden',
                zIndex: 3,
                backgroundImage: 'url(https://nordicchoicehotels.papirfly.no/readimage.aspx/asset.jpg?pubid=JUdvbpjy2inpjg0Iheu1ig&quality=9)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 8,
                  background: theme => theme.palette.primary.main,
                  mixBlendMode: 'hue',
                  opacity: 1,
                  zIndex: 1,
                }
              }} />
            </Box>
          </Box>
        </Card>
      </Fade>
    </Box>
  );
};

export default LoginPage; 